import React from 'react';
import { Select, Input, Popover, Button, Modal } from 'antd';
import { BsPlusLg } from 'react-icons/bs';
import { TASK_INSTANCE_STATE_FILTERS } from '../../constants';
import { ClearTaskDialog } from '../../dag-detail/clear-task-dialog';
import { InstancesTable } from './instances-table';
import { useTaskInstancesTab } from './use-task-instances-tab';
import styles from '../../styles.module.scss';

const { Option } = Select;

type TaskInstancesTabProps = {
    dagId: string;
    runId: string;
    refreshKey: number;
};

const CONFIRM_COPY: Record<string, { title: string; okColor: string; okGlow: string }> = {
    success: { title: 'Mark task as success?', okColor: '#08c46a', okGlow: 'rgba(8, 196, 106, 0.72)' },
    failed: { title: 'Mark task as failed?', okColor: '#ff4d4f', okGlow: 'rgba(255, 77, 79, 0.72)' },
    delete: { title: 'Delete this task instance? This cannot be undone.', okColor: '#ff4d4f', okGlow: 'rgba(255, 77, 79, 0.72)' },
};

/*
 * DAG-run detail's Task Instances tab — filters, table, per-row actions, and Clear Task dialog
 */
export function TaskInstancesTab({ dagId, runId, refreshKey }: TaskInstancesTabProps) {
    const {
        isLoading,
        stateFilter,
        draftFilters,
        hasActiveFilters,
        pagedInstances,
        filteredCount,
        page,
        setPage,
        totalPages,
        clearDialogTask,
        setClearDialogTask,
        confirmAction,
        setConfirmAction,
        handleStateFilterChange,
        handleOpenFilters,
        handleDraftChange,
        handleApplyFilters,
        handleClearFilters,
        handleConfirmTaskAction,
        handleTaskClick,
        refetch,
    } = useTaskInstancesTab(dagId, runId, refreshKey);

    return (
        <div className={styles.card}>
            <div className={styles.filterRow}>
                <h3 style={{ margin: 0, color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 0.25 }}>{`Task Instances (${filteredCount})`}</h3>
                {hasActiveFilters && (
                    <Button type="link" onClick={handleClearFilters}>
                        Clear filters
                    </Button>
                )}
                <div className={styles.filterRowEnd}>
                    <Select value={stateFilter || undefined} placeholder="State" allowClear style={{ width: 160 }} onChange={(value) => handleStateFilterChange(value ?? '')}>
                        {TASK_INSTANCE_STATE_FILTERS.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                    <Popover
                        overlayClassName="airflowPopoverOverlay"
                        trigger="click"
                        onVisibleChange={(visible) => visible && handleOpenFilters()}
                        content={
                            <div style={{ width: 260, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <Input placeholder="Task ID" value={draftFilters.taskId} onChange={(e) => handleDraftChange('taskId', e.target.value)} />
                                <Input placeholder="Operator" value={draftFilters.operator} onChange={(e) => handleDraftChange('operator', e.target.value)} />
                                <Input type="number" placeholder="Map Index" value={draftFilters.mapIndex} onChange={(e) => handleDraftChange('mapIndex', e.target.value)} />
                                <Input type="number" placeholder="Try Number" value={draftFilters.tryNumber} onChange={(e) => handleDraftChange('tryNumber', e.target.value)} />
                                <Button type="primary" onClick={handleApplyFilters}>
                                    Apply
                                </Button>
                            </div>
                        }
                    >
                        <Button>
                            <BsPlusLg style={{ marginRight: 6 }} />
                            Filter
                        </Button>
                    </Popover>
                </div>
            </div>

            <InstancesTable
                tasks={pagedInstances}
                isLoading={isLoading}
                onTaskClick={handleTaskClick}
                onClear={setClearDialogTask}
                onMarkSuccess={(task) => setConfirmAction({ type: 'success', task })}
                onMarkFailed={(task) => setConfirmAction({ type: 'failed', task })}
                onDeleteRequest={(task) => setConfirmAction({ type: 'delete', task })}
            />

            <div className={styles.paginationRow} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#666a8d', fontSize: 13 }}>{`Page ${page} of ${totalPages}`}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        Prev
                    </Button>
                    <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </div>
            </div>

            {clearDialogTask && <ClearTaskDialog dagId={dagId} task={clearDialogTask} onClose={() => setClearDialogTask(null)} onCleared={refetch} />}

            {confirmAction && (
                <Modal
                    visible
                    className="airflowDashboardRoot"
                    title={CONFIRM_COPY[confirmAction.type].title}
                    onCancel={() => setConfirmAction(null)}
                    onOk={handleConfirmTaskAction}
                    okText="Confirm"
                    okButtonProps={{
                        style: {
                            backgroundColor: CONFIRM_COPY[confirmAction.type].okColor,
                            borderColor: CONFIRM_COPY[confirmAction.type].okColor,
                            boxShadow: `0 1px 4px 0 ${CONFIRM_COPY[confirmAction.type].okGlow}`,
                        },
                    }}
                />
            )}
        </div>
    );
}
